import { useState, useCallback, useMemo } from 'react'
import { MessageSquare, Zap, Trash2, RefreshCw, Settings, Bug, Sparkles, Palette, Database, Network, Image } from 'lucide-react'
import React from 'react'

export interface CommandItem {
  id: string
  label: string
  description?: string
  action: () => void
  icon?: React.ReactNode
  category?: string
}

interface UseCommandsProps {
  onOpenChat: () => void
  onClearChat: () => void
  onRefreshProviders: () => void
  onOpenSettings: () => void
  isAdmin?: boolean
}

export const useCommands = ({ onOpenChat, onClearChat, onRefreshProviders, onOpenSettings, isAdmin = false }: UseCommandsProps) => {
  const [customCommands, setCustomCommands] = useState<CommandItem[]>([])

  const builtInCommands: CommandItem[] = useMemo(() => [
    {
      id: 'open-chat',
      label: 'Open Chat',
      description: 'Start a conversation with AI providers',
      action: onOpenChat,
      icon: React.createElement(MessageSquare, { className: "w-4 h-4" }),
      category: 'Chat'
    },
    {
      id: 'clear-chat',
      label: 'Clear Chat',
      description: 'Clear all chat messages',
      action: onClearChat,
      icon: React.createElement(Trash2, { className: "w-4 h-4" }),
      category: 'Chat'
    },
    {
      id: 'refresh-providers',
      label: 'Refresh Providers',
      description: 'Reload available AI providers',
      action: onRefreshProviders,
      icon: React.createElement(RefreshCw, { className: "w-4 h-4" }),
      category: 'Providers'
    },
    {
      id: 'open-settings',
      label: 'Settings',
      description: 'Configure Zen Creator preferences and default AI models',
      action: onOpenSettings,
      icon: React.createElement(Settings, { className: "w-4 h-4" }),
      category: 'Settings'
    },
    {
      id: 'playground-debug',
      label: 'Debug Panel',
      description: 'Open API debug panel to inspect requests and responses',
      action: () => {
        window.location.href = '?page=zen-simple&debug=true'
      },
      icon: React.createElement(Bug, { className: "w-4 h-4" }),
      category: 'Debug'
    },
    {
      id: 'playground',
      label: 'Playground',
      description: 'Open the interactive code playground',
      action: () => {
        window.location.href = '?page=zen-simple'
      },
      icon: React.createElement(Zap, { className: "w-4 h-4" }),
      category: 'Navigation'
    },
    {
      id: 'zen-creator',
      label: 'Zen Creator',
      description: 'Open classic Zen Creator interface',
      action: () => {
        window.location.href = '?page=zen-creator'
      },
      icon: React.createElement(Sparkles, { className: "w-4 h-4" }),
      category: 'Navigation'
    },
    {
      id: 'zen-mock',
      label: 'Zen Mock (Design Preview)',
      description: 'Open modern design mock of Zen Creator',
      action: () => {
        window.location.href = '?page=zen-mock'
      },
      icon: React.createElement(Sparkles, { className: "w-4 h-4" }),
      category: 'Design'
    },
    {
      id: 'home',
      label: 'Home',
      description: 'Return to SyntaxLens home page',
      action: () => {
        window.location.href = '/'
      },
      icon: React.createElement(Palette, { className: "w-4 h-4" }),
      category: 'Navigation'
    },
    {
      id: 'concept-graph',
      label: 'Concept Graph',
      description: 'Explore programming concepts across languages (C, JavaScript)',
      action: () => {
        window.location.href = '?page=concept-graph'
      },
      icon: React.createElement(Network, { className: "w-4 h-4" }),
      category: 'Navigation'
    },
    {
      id: 'image-lab',
      label: 'Image Prompt Lab',
      description: 'Experiment with AI diagram generation for code insights',
      action: () => {
        window.location.href = '?page=image-lab'
      },
      icon: React.createElement(Image, { className: "w-4 h-4" }),
      category: 'Tools'
    },
    {
      id: 'admin-shares',
      label: 'Admin: Shares',
      description: 'View and manage all shareable URLs',
      action: () => {
        window.location.href = '?page=admin-shares'
      },
      icon: React.createElement(Database, { className: "w-4 h-4" }),
      category: 'Admin'
    },
    {
      id: 'admin-feedback',
      label: 'Admin: Feedback',
      description: 'View and manage user feedback',
      action: () => {
        window.location.href = '?page=admin-feedback'
      },
      icon: React.createElement(MessageSquare, { className: "w-4 h-4" }),
      category: 'Admin'
    }
  ], [onOpenChat, onClearChat, onRefreshProviders, onOpenSettings])

  // Filter commands based on admin mode
  const filteredCommands = useMemo(() => {
    if (isAdmin) {
      return builtInCommands
    }
    // Non-admin users: filter out admin-only commands
    return builtInCommands.filter(cmd =>
      cmd.category !== 'Admin' &&
      cmd.id !== 'playground-debug' &&
      cmd.id !== 'refresh-providers'
    )
  }, [builtInCommands, isAdmin])

  const allCommands = useMemo(() => [
    ...filteredCommands,
    ...customCommands
  ], [filteredCommands, customCommands])

  const registerCommand = useCallback((command: CommandItem) => {
    setCustomCommands(prev => {
      // Remove existing command with same id if it exists
      const filtered = prev.filter(cmd => cmd.id !== command.id)
      return [...filtered, command]
    })
  }, [])

  const unregisterCommand = useCallback((commandId: string) => {
    setCustomCommands(prev => prev.filter(cmd => cmd.id !== commandId))
  }, [])

  const getCommandsByCategory = useCallback((category?: string) => {
    if (!category) return allCommands
    return allCommands.filter(cmd => cmd.category === category)
  }, [allCommands])

  const findCommand = useCallback((commandId: string) => {
    return allCommands.find(cmd => cmd.id === commandId)
  }, [allCommands])

  return {
    commands: allCommands,
    builtInCommands,
    customCommands,
    registerCommand,
    unregisterCommand,
    getCommandsByCategory,
    findCommand
  }
}